import React from 'react';
import { Plus, RotateCw, AlertCircle } from 'lucide-react';
import DeviceCard from './DeviceCard';

/**
 * ClimateTab Component
 * Displays climate monitoring and control devices
 * 
 * @param {Object} props Component properties
 * @param {Array} props.devices Array of climate device objects
 * @param {Array} props.deviceTypes Array of device type definitions
 * @param {Function} props.onAddDevice Function to call when adding a new device
 * @param {Function} props.onEditDevice Function to call when editing a device
 * @param {Function} props.onDeleteDevice Function to call when deleting a device
 * @param {Function} props.onToggleDevice Function to call when toggling device state
 * @param {Function} props.onRefresh Function to call when refreshing data
 * @param {Date|null} props.lastRefresh Timestamp of last data refresh
 */
const ClimateTab = ({
  devices,
  deviceTypes,
  onAddDevice,
  onEditDevice,
  onDeleteDevice,
  onToggleDevice,
  onRefresh,
  lastRefresh
}) => {
  // Sort devices alphabetically by name
  const sortedDevices = [...devices].sort((a, b) => 
    a.deviceName.localeCompare(b.deviceName)
  );

  // Helper to find device type details
  const getDeviceType = (deviceTypeId) => {
    return deviceTypes.find(type => type.typeID === deviceTypeId);
  };
  
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5>Climate Control Devices</h5>
        <div className="btn-group">
          <button
            className="btn btn-outline-secondary"
            onClick={onRefresh}
            title="Refresh data"
          >
            <RotateCw size={16} className="me-1" />
            Refresh
            {lastRefresh && 
              <small className="ms-1 text-muted">
                ({new Date(lastRefresh).toLocaleTimeString()})
              </small>
            }
          </button>
          <button
            className="btn btn-outline-primary"
            onClick={onAddDevice}
          >
            <Plus size={16} className="me-1" />
            Add Climate Device
          </button>
        </div>
      </div>

      <div className="row">
        {sortedDevices.length === 0 ? (
          <div className="col-12">
            <div className="alert alert-info">
              <AlertCircle size={18} className="me-2" />
              No climate control devices configured yet. Click "Add Climate Device" to add your first device.
            </div>
          </div>
        ) : (
          sortedDevices.map((device) => (
            <DeviceCard
              key={device.deviceID}
              device={device}
              deviceType={getDeviceType(device.typeID)}
              onEdit={() => onEditDevice(device)}
              onDelete={() => onDeleteDevice(device.deviceID)}
              onToggle={onToggleDevice}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default ClimateTab;